import {
  CATEGORIES_COLLECTION,
  PRODUCTS_COLLECTION,
  STOCK_CHANGES_COLLECTION,
  TEST_HUB_URL,
  VARIANTS_COLLECTION,
} from '@/constants/constants';
import { Platform } from '@/enums/platform';
import { Product, ProductWithVariants, Variant } from '@/types/products.type';
import { getCollection } from '@/utils/helpers/getCollectionByName';
import { BSON } from 'realm-web';
import { deleteImageByUrl } from '../Attachments/attachments.service';
import { InsertOneResult } from '@/types/types';
import { BaseActionType } from '@/types';
import { apiFetch } from '@/utils/apiWrapper';

export async function fetchProductsByCompany(
  companyId: string,
  page: number,
  pageSize: number,
  filters: Record<string, any>,
): Promise<Product[] | undefined> {
  try {
    const products = getCollection(PRODUCTS_COLLECTION);

    const validPage = Math.max(page, 1);

    const validFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([key, value]) => value !== undefined && key !== 'sku' && key !== 'name',
      ),
    );

    const extraMatchBySkuOrName = [
      {
        $unwind: {
          path: '$variants',
          includeArrayIndex: 'variantIndex',
        },
      },
      {
        $match: {
          $or: [
            {
              'variants.sku': {
                $regex: filters.name,
                $options: 'i', // Case-insensitive regex match
              },
            },
            {
              name: {
                $regex: filters.name,
                $options: 'i', // Case-insensitive regex match for product name
              },
            },
          ],
        },
      },
      {
        $group: {
          _id: '$variants.productId',
          variants: {
            $push: '$variants',
          },
          name: {
            $first: '$name',
          },
          categories: {
            $first: '$categories',
          },
          description: {
            $first: '$description',
          },
          images: {
            $first: '$images',
          },
          status: {
            $first: '$status',
          },
          outlets: {
            $first: '$outlets',
          },
          maxPrice: { $first: '$maxPrice' },
          minPrice: { $first: '$minPrice' },
          companyId: { $first: '$companyId' },
          productType: { $first: '$productType' },
          isDeleted: { $first: '$isDeleted' },
          taxTable: { $first: '$taxTable' },
          productId: { $first: '$_id' },
          externalId: { $first: '$externalId' },
        },
      },
    ];
    const productsByCompany = await products?.aggregate([
      {
        $match: {
          companyId: new BSON.ObjectID(companyId),
          ...validFilters,
        },
      },
      {
        $skip: (validPage - 1) * pageSize,
      },
      {
        $limit: pageSize,
      },
      {
        $lookup: {
          from: 'variants',
          let: { productId: '$_id' },
          pipeline: [
            {
              $match: {
                $expr: {
                  $and: [
                    { $eq: ['$productId', '$$productId'] }, // Match the productId
                    { $eq: ['$companyId', new BSON.ObjectID(companyId)] }, // Match the companyId
                  ],
                },
              },
            },
          ],
          as: 'variants',
        },
      },
      ...(filters.name ? extraMatchBySkuOrName : []),
      {
        $lookup: {
          from: 'categories',
          localField: 'categories',
          foreignField: '_id',
          as: 'categoryObjects',
        },
      },
      {
        $addFields: {
          categoryNames: {
            $map: {
              input: '$categoryObjects',
              as: 'category',
              in: '$$category.name',
            },
          },
        },
      },
    ]);

    const deserializedProducts = BSON.EJSON.deserialize(productsByCompany) as Product[];
    return deserializedProducts;
  } catch (err) {
    console.error('Error fetching products', err);
  }
}

export async function getTotalCountOfProducts(companyId: string, filters: Record<string, any>) {
  try {
    const products = getCollection(PRODUCTS_COLLECTION);

    const validFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([key, value]) => value !== undefined && key !== 'sku' && key !== 'name',
      ),
    );
    const query: any = {
      companyId: new BSON.ObjectID(companyId),
      ...validFilters,
    };

    const totalCount = await products?.count(query);
    return totalCount;
  } catch (err) {
    console.error('Error fetching products', err);
  }
}

export function fetchCategories(companyId: string) {
  try {
    const categories = getCollection(CATEGORIES_COLLECTION);
    return categories?.find({ companyId: new BSON.ObjectID(companyId) });
  } catch (err) {
    console.error('Error fetching categories', err);
  }
}

export async function fetchProductById(id: string): Promise<Product | undefined> {
  try {
    const products = getCollection('products');
    const product = await products?.aggregate([
      { $match: { _id: new BSON.ObjectID(id) } },
      {
        $lookup: {
          from: 'variants',
          localField: '_id',
          foreignField: 'productId',
          as: 'variants',
        },
      },
    ]);
    return BSON.EJSON.deserialize(product[0]) as ProductWithVariants;
  } catch (err) {
    console.error('Error fetching product', err);
  }
}

export const createProduct = async (
  data: Record<string, any>,
): Promise<InsertOneResult<'_id'> | undefined> => {
  try {
    const { variants, sku, price, costPrice, manageStock, ...restProduct } = data;
    const productsCollection = getCollection(PRODUCTS_COLLECTION);
    const variantsCollection = getCollection(VARIANTS_COLLECTION);
    console.log('restProduct', restProduct);
    const newProduct = await productsCollection?.insertOne({
      _id: new BSON.ObjectId(),
      categories:
        restProduct.categories?.map((category: string) => new BSON.ObjectID(category)) ?? [],
      taxTable: new BSON.ObjectID(restProduct.taxTable as string),
      companyId: new BSON.ObjectID(restProduct.companyId),
      productType: variants?.length > 0 ? 'variable' : 'simple',
      source: Platform.STAND_ALONE,
      name: restProduct.name,
      description: restProduct.description,
      images: restProduct.images ?? [],
      status: restProduct.status ?? 'active',
      outlets: restProduct?.outlets
        ? restProduct.outlets.map((outlet: string) => new BSON.ObjectID(outlet))
        : [],
    });

    if (!newProduct) return;

    const defaultVariantValues = {
      productId: newProduct.insertedId,
      companyId: new BSON.ObjectID(restProduct.companyId),
      attributes: [],
      images: [],
      allowBackorder: false,
    };

    if (!variants?.length) {
      await variantsCollection?.insertOne({
        _id: new BSON.ObjectId(),
        ...defaultVariantValues,
        price,
        manageStock: manageStock ?? false,
        inventory: manageStock
          ? restProduct?.outlets.map((outlet: string) => ({
              outletId: new BSON.ObjectID(outlet),
              stock: 0,
            }))
          : [],
        ...(sku && { sku }),
        ...(costPrice && { costPrice }),
      });
    } else {
      const variantsWithProductId = variants.map((variant: any) => {
        const { _id, ...restVariant } = variant;
        const variantBody = restVariant;
        if (restVariant?.manageStock) {
          variantBody.inventory = restProduct?.outlets.map((outlet: string) => ({
            outletId: new BSON.ObjectID(outlet),
            stock: 0,
          }));
        }
        return {
          _id: new BSON.ObjectId(_id),
          ...defaultVariantValues,
          ...variantBody,
        };
      });
      await variantsCollection?.insertMany(variantsWithProductId);
    }
    return BSON.EJSON.deserialize(newProduct) as InsertOneResult<'_id'>;
  } catch (err) {
    console.error('Error creating product', err);
  }
};

export async function updateProduct(data: Product) {
  try {
    const productsCollection = getCollection(PRODUCTS_COLLECTION);
    const updatedProduct = await productsCollection?.findOneAndUpdate(
      { _id: new BSON.ObjectID(data._id) },
      {
        $set: {
          categories:
            data?.categories?.map((category: string) => new BSON.ObjectID(category)) ?? [],
          taxTable: new BSON.ObjectID(data.taxTable as string),
          status: data.status,
          name: data.name,
          description: data.description,
          images: data?.images ?? [],
          outlets: data?.outlets
            ? data.outlets.map((outlet: string) => new BSON.ObjectID(outlet))
            : [],
        },
      },
      { upsert: true, returnNewDocument: true },
    );
    return BSON.EJSON.deserialize(updatedProduct) as Product;
  } catch (err) {
    console.error('Error updating product', err);
  }
}

export const fetchProductVariants = async (productId: string): Promise<Variant[]> => {
  try {
    const variantsCollection = getCollection('variants');
    const variants = await variantsCollection?.find({
      productId: new BSON.ObjectID(productId),
    });
    if (!variants) return [];
    return BSON.EJSON.deserialize(variants) as Variant[];
  } catch (err) {
    console.error('Error fetching product variants', err);
    return [];
  }
};

export const updateProductVariant = async ({
  additions,
  changes,
  deletions,
  productId,
  companyId,
}: {
  changes: Array<{ _id: string; changes: Partial<Variant> }>;
  additions: Variant[];
  deletions: string[];
  productId: string;
  companyId: string;
}) => {
  try {
    const variantsCollection = getCollection('variants');
    const productsCollection = getCollection('products');

    if (additions.length > 0) {
      await variantsCollection?.insertMany(
        additions.map((data) => {
          delete data.name;
          return {
            manageStock: false,
            ...data,
            _id: new BSON.ObjectID(data._id),
            productId: new BSON.ObjectID(productId),
            companyId: new BSON.ObjectID(companyId),
          };
        }),
      );

      await productsCollection?.updateOne(
        { _id: new BSON.ObjectID(productId) },
        { $set: { productType: 'variable' } },
      );
    }
    await Promise.all(
      changes.map(async (data) => {
        const { inventory, ...restChanges } = data.changes;
        const updatedVariant = await variantsCollection?.findOneAndUpdate(
          { _id: new BSON.ObjectID(data._id) },
          {
            ...(Boolean(restChanges) && { $set: restChanges }),
            ...(inventory && {
              $addToSet: { inventory: { $each: inventory } },
            }),
          },

          { upsert: true, returnNewDocument: true },
        );
        return updatedVariant;
      }),
    );

    if (deletions.length > 0) {
      await variantsCollection?.deleteMany({
        _id: { $in: deletions.map((id) => new BSON.ObjectID(id)) },
      });
    }
  } catch (err) {
    console.error('Error updating product variant', err);
  }
};

export const updateVariationImage = async (variantId: string, image: string) => {
  try {
    const variantsCollection = getCollection('variants');
    const updatedVariant = await variantsCollection?.findOneAndUpdate(
      { _id: new BSON.ObjectID(variantId) },
      {
        $push: {
          images: {
            $each: [image],
            $position: 0,
          },
        },
      },
      { returnNewDocument: true },
    );
    return BSON.EJSON.deserialize(updatedVariant) as Variant;
  } catch (err) {
    console.error('Error updating variation image', err);
  }
};

export const deleteVariationImage = async (variantId: string, image: string) => {
  try {
    const variantsCollection = getCollection('variants');
    const updatedVariant = await variantsCollection?.findOneAndUpdate(
      { _id: new BSON.ObjectID(variantId) },
      {
        $pull: {
          images: image,
        },
      },
      { upsert: true, returnNewDocument: true },
    );
    await deleteImageByUrl(image);
    return BSON.EJSON.deserialize(updatedVariant) as Variant;
  } catch (err) {
    console.error('Error deleting variation image', err);
  }
};

export const updateProductImages = async (productId: string, images: string[]) => {
  try {
    const productsCollection = getCollection('products');
    const updatedProduct = await productsCollection?.findOneAndUpdate(
      { _id: new BSON.ObjectID(productId) },
      { $set: { images } },
      { upsert: true, returnNewDocument: true },
    );
    return BSON.EJSON.deserialize(updatedProduct) as Product;
  } catch (err) {
    console.error('Error updating product images', err);
  }
};

export async function findVariantsByAttribute(
  companyId: string,
  attributeName: string,
  attributeValue: string | null,
) {
  try {
    const productCollection = getCollection('products');
    const variantsCollection = getCollection('variants');

    // Step 1: Find products by companyId and source
    const products = await productCollection?.find({
      companyId: new BSON.ObjectID(companyId),
      source: Platform.STAND_ALONE,
    });

    if (!products || products.length === 0) {
      return []; // No products found
    }

    // Step 2: Find variants for each product where attributes match
    const productIds = products.map((product) => product._id);
    const variants = await variantsCollection?.find({
      productId: { $in: productIds },
      attributes: {
        $elemMatch: {
          name: attributeName,
          ...(attributeValue && { value: attributeValue }),
        },
      },
    });
    if (!variants) {
      return []; // No variants found
    }
    // Step 3: Return the variants found
    return BSON.EJSON.deserialize(variants) as Variant[];
  } catch (err) {
    console.error('Error fetching variants by attribute', err);
    return [];
  }
}

export const deleteVariationsByIds = async (variantIds: string[]) => {
  try {
    const variantsCollection = getCollection('variants');
    await variantsCollection?.deleteMany({
      _id: { $in: variantIds.map((id) => new BSON.ObjectID(id)) },
    });
  } catch (err) {
    console.error('Error deleting variations', err);
  }
};

export const adjustInventory = async ({
  productId,
  variantId,
  userId,
  outletId,
  companyId,
  quantity,
  specificAction,
  baseAction,
}: {
  productId: string;
  variantId: string;
  companyId: string;
  outletId: string;
  userId: string;
  specificAction: string;
  baseAction: BaseActionType;
  quantity: number;
}) => {
  try {
    const res = await apiFetch(`${TEST_HUB_URL}product/update-stock/${productId}/${variantId}`, {
      method: 'PATCH',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        companyId,
        outletId,
        userId,
        quantity,
        specificAction,
        baseAction,
      }),
    });
    if (!res) return;

    const data: boolean = await res.json();
    if (data) {
      return {
        variantId,
        outletId,
        quantity,
        baseAction,
      };
    }
  } catch (err) {
    console.error('Error adjusting inventory', err);
  }
};

export const getProductHistory = async (productId: string) => {
  try {
    const productCollection = getCollection(STOCK_CHANGES_COLLECTION);

    const res = await productCollection?.find({
      productId: new BSON.ObjectID(productId),
    });
    if (!res) return;
    return BSON.EJSON.deserialize(res);
  } catch (err) {
    console.error('Error fetching product history', err);
  }
};
